import { VFC } from "react";
// Libraries
import { Route, Switch, Redirect, RouteComponentProps } from "react-router-dom";
import { useQuery } from "@apollo/client";
// Utils
import { GET_GROUPS_COUNT } from "groups/api/queries";
import { GET_ANIMAL_BY_ID } from "api/MyLivestock/Animal/queries";
import { GetAnimalById, GetAnimalByIdVariables } from "api/MyLivestock/Animal/types/GetAnimalById";
import { getAnimalViewBreadCrumbs } from "helpers/myLivestock";
import { getBUFromLocalStorage } from "helpers/storage";
import { useHasFeature, useStateSpecies } from "hooks";
import {
  ANIMALS,
  ANIMAL_VIEW,
  ANIMAL_VIEW_PERFORMANCE,
  ANIMAL_VIEW_HISTORY,
  ANIMAL_VIEW_OFFSPRING,
  BU_ID,
} from "constants/Routes";
// Components
import { AnimalInfoBlock, AnimalOffspring, AnimalPerformanceBlock as PerformanceBlock } from "components/MyLivestock";
import AnimalChart from "pages/MyLivestock/Animals/AnimalChart";
import AnimalHistory from "pages/MyLivestock/Activities/AnimalHistory";
import AnimalChartHistoryBlock from "pages/MyLivestock/Animals/AnimalChartHistoryBlock";
import { Breadcrumbs, Flex, LoadingOverlay, Header, PageHeader, PageContent, Spacer, Tabs } from "components";
// Resources
import styles from "./AnimalView.module.scss";

interface RouteParams {
  fromPage: string;
  id: string;
  tab: string;
}

type AnimalViewProps = RouteComponentProps<RouteParams>;

interface GroupsCountProps {
  groupsExtended: {
    groupsCount: number;
  };
}

function getCurrentTabId(tabName: string): number {
  switch (tabName) {
    case "history":
      return 1;
    case "offspring":
      return 2;
    default:
      return 0;
  }
}

const AnimalView: VFC<AnimalViewProps> = ({ match }) => {
  const { active: speciesActive } = useStateSpecies();

  const isUseActivityChartQuery: boolean = useHasFeature("USE_ACTIVITY_CHART_QUERY");
  const animalOffspringEnabled = useHasFeature("SHOW_ANIMAL_OFFSPRING");

  const {
    params: { fromPage, id, tab },
    url,
  } = match;

  const {
    data: animalData,
    loading,
    error,
  } = useQuery<GetAnimalById, GetAnimalByIdVariables>(GET_ANIMAL_BY_ID, {
    variables: {
      id,
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });
  const { data: groupsExtendedData } = useQuery(GET_GROUPS_COUNT, {
    variables: {
      businessUnitId: getBUFromLocalStorage(),
    },
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-only",
  });

  const { groupsExtended }: GroupsCountProps = {
    ...groupsExtendedData,
  };
  const { groupsCount: numberOfGroups } = {
    ...groupsExtended,
  };
  const { animal } = {
    ...animalData,
  };

  const isArchived = !!animal?.deadAt || !!animal?.dateLeftFarm;

  const ANIMAL_VIEW_SUBTABS = [
    {
      key: 0,
      name: "Performance",
      route: `${ANIMAL_VIEW_PERFORMANCE.replace(":id", id).replace(":fromPage", fromPage)}`,
    },
    {
      key: 1,
      name: "History",
      route: `${ANIMAL_VIEW_HISTORY.replace(":id", id).replace(":fromPage", fromPage)}`,
    },
    {
      key: 2,
      name: "Offspring",
      route: `${ANIMAL_VIEW_OFFSPRING.replace(":id", id).replace(":fromPage", fromPage)}`,
    },
  ];

  if (error) return <Redirect to={ANIMALS} />;

  if (!animal) {
    if (loading) return <LoadingOverlay customStyles={styles.loadingOverlay} />;

    return <Redirect to={ANIMALS} />;
  }

  if (speciesActive) {
    const isAnimalDifferentSpecies = animal.animalType?.id !== speciesActive.id;
    if (isAnimalDifferentSpecies) return <Redirect to={ANIMALS} />;
  }
  return (
    <>
      <PageHeader>
        <Breadcrumbs rootPaths={getAnimalViewBreadCrumbs(url)} currentPath="Animal Information" />

        <Header backButton title="Animal Information" />

        <AnimalInfoBlock animal={animal} canAddToGroup={numberOfGroups > 0} isArchived={isArchived} />

        {animalOffspringEnabled && <Tabs tabs={ANIMAL_VIEW_SUBTABS} selectedTab={getCurrentTabId(tab)} />}
      </PageHeader>

      <PageContent>
        {isUseActivityChartQuery ? (
          <AnimalChartHistoryBlock />
        ) : (
          <Flex container className={styles.animal_view__container}>
            <Flex item itemGutter xs="fill">
              <Spacer baselineHeight={3} />

              <Switch>
                <Route
                  exact
                  path={`${BU_ID}${ANIMAL_VIEW_PERFORMANCE}`}
                  render={(): JSX.Element => (
                    <>
                      <PerformanceBlock animal={animal} isArchived={isArchived} showHeading={!animalOffspringEnabled} />
                      <Spacer baselineHeight={3} />
                      <AnimalChart isArchived={isArchived} />
                    </>
                  )}
                />
                {animalOffspringEnabled && (
                  <Route
                    path={`${BU_ID}${ANIMAL_VIEW_HISTORY}`}
                    render={(): JSX.Element => <AnimalHistory animalId={animal.id} />}
                  />
                )}
                {animalOffspringEnabled && (
                  <Route
                    path={`${BU_ID}${ANIMAL_VIEW_OFFSPRING}`}
                    render={(): JSX.Element => <AnimalOffspring animalId={animal.id} />}
                  />
                )}
                <Redirect exact from={`${BU_ID}${ANIMAL_VIEW}`} to={`${BU_ID}${ANIMAL_VIEW_PERFORMANCE}`} />
              </Switch>

              {!animalOffspringEnabled && (
                <>
                  <Spacer baselineHeight={3} />
                  <AnimalHistory animalId={animal.id} showHeading />
                </>
              )}

              <Spacer baselineHeight={3} />
            </Flex>
          </Flex>
        )}
      </PageContent>
    </>
  );
};

export default AnimalView;
